/**
 * Shared `config.file` for react-player video instances.
 *
 * `hlsVersion` overrides react-player's bundled default (1.1.4, from 2021),
 * which fails with `hlsError` on modern Cloudflare Stream manifests
 * (EXT-X-VERSION:6, independent segments, audio groups). Loading a current
 * hls.js from the CDN resolves the playback errors.
 */
export const VIDEO_PLAYER_FILE_CONFIG = {
  hlsVersion: "1.5.20",
  attributes: {
    playsInline: true,
    muted: true,
  },
} as const;

/**
 * Derives the Cloudflare Stream poster (thumbnail) image for a video URL.
 *
 * Cloudflare Stream exposes a JPEG thumbnail alongside the HLS/DASH manifest:
 *   .../{id}/manifest/video.m3u8  ->  .../{id}/thumbnails/thumbnail.jpg
 *
 * Returns `null` for URLs that aren't Cloudflare Stream manifests so callers
 * can fall back to no poster.
 */
export function getVideoPosterUrl(url: string): string | null {
  const match = url.match(/^(.*)\/manifest\/video\.(m3u8|mpd)(\?.*)?$/);
  if (!match) return null;
  return `${match[1]}/thumbnails/thumbnail.jpg`;
}

/**
 * Forces muted autoplay on a react-player instance.
 *
 * React does not reliably reflect the `muted` prop onto the underlying
 * `<video>` element (see facebook/react#10389), so browsers block autoplay
 * because the element is treated as un-muted. This helper grabs the internal
 * media element, sets both `defaultMuted` (which reflects the `muted`
 * attribute) and `muted`, then attempts playback — retrying on `canplay` in
 * case the first attempt fires before enough data is buffered.
 */
export function enableMutedAutoplay(player: {
  getInternalPlayer: () => unknown;
}): void {
  const video = player.getInternalPlayer() as HTMLVideoElement | null;
  if (!video || typeof video.play !== "function") return;

  video.defaultMuted = true;
  video.muted = true;
  video.setAttribute("muted", "");

  const tryPlay = () => {
    void video.play?.().catch(() => {});
  };

  tryPlay();
  video.addEventListener("canplay", tryPlay, { once: true });
}

type HlsErrorData = {
  type?: string;
  details?: string;
  fatal?: boolean;
  reason?: string;
};

type HlsInstance = {
  startLoad: () => void;
  recoverMediaError: () => void;
};

type HlsStatic = {
  ErrorTypes: { NETWORK_ERROR: string; MEDIA_ERROR: string };
};

/**
 * Error handler for react-player HLS sources.
 *
 * react-player forwards every hls.js `ERROR` event here as
 * `(eventName, data, hls, Hls)` — including non-fatal events that hls.js
 * recovers from on its own. Logging those as failures is just noise. For
 * fatal errors we apply hls.js's documented recovery (reload for network,
 * `recoverMediaError` for media) before giving up.
 */
export function handleVideoError(
  eventName: unknown,
  data?: HlsErrorData,
  hls?: HlsInstance,
  Hls?: HlsStatic,
): void {
  // Plain <video> error events (non-HLS) arrive without hls data.
  if (!data || typeof data !== "object") return;

  // Non-fatal hls.js errors are auto-recovered — ignore them.
  if (data.fatal !== true) return;

  if (hls && Hls) {
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
      hls.startLoad();
      return;
    }
    if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
      hls.recoverMediaError();
      return;
    }
  }

  console.error("Video playback error:", data.type, data.details, data.reason);
}
